import { useCallback, useState } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { useBulkUpdateTabsMutation } from "@/store/api/dashboard-api";
import { computeBulkUpdatePayload } from "./group-utils";

export interface StructureSaveError {
  message: string;
  retry: () => void;
}

/**
 * Persists any structural change (reorder, group create/rename/delete/move)
 * as a single bulk PATCH. The UI update is optimistic; a failure rolls back
 * to the previous structure and surfaces a retryable error, since the bulk
 * endpoint has no partial-failure semantics of its own to reconcile against.
 */
export const useTabBulkSave = (
  dashboardId: string | undefined,
  onSave: (tabs: TabData[]) => void,
) => {
  const [bulkUpdateTabs] = useBulkUpdateTabsMutation();
  const [saveError, setSaveError] = useState<StructureSaveError | null>(null);

  const saveStructure = useCallback(
    async (nextTabs: TabData[], previousTabs: TabData[]) => {
      setSaveError(null);
      onSave(nextTabs);

      if (!dashboardId) return;

      // If every remaining tab is still optimistic (temp-*), there's nothing
      // real to persist yet — the DTO requires at least one item, and there's
      // no tabId the backend would recognize anyway.
      const tabsToUpdate = computeBulkUpdatePayload(nextTabs);
      if (tabsToUpdate.length === 0) return;

      try {
        await bulkUpdateTabs({ dashboardId, tabsToUpdate }).unwrap();
      } catch (e) {
        console.error("Failed to save tab structure:", e);
        onSave(previousTabs);
        setSaveError({
          message: "Couldn't save your changes. Please try again.",
          retry: () => void saveStructure(nextTabs, previousTabs),
        });
      }
    },
    [dashboardId, bulkUpdateTabs, onSave],
  );

  const dismissSaveError = useCallback(() => setSaveError(null), []);

  return { saveStructure, saveError, dismissSaveError } as const;
};
